<template>
  <div
    class="flex flex-col gap-5 py-6 bg-utility-blue-50 border-l border-[#EAECF0] rounded-l-md overflow-hidden"
    data-testid="aki-patient-infobox"
  >
    <!-- Header -->
    <div class="flex flex-col gap-1 px-6">
      <div class="flex gap-2 justify-between items-start">
        <p class="text-primary-text text-lg font-semibold" data-testid="infobox-patient-name">
          {{ getFormattedPatientName(caze.patient) }}
        </p>
        <button data-testid="btn-close-infobox" @click="$emit('close-infobox')">
          <VIcon color="tree-node" icon="close" size="20" />
        </button>
      </div>

      <div class="flex flex-col gap-1 text-subtitle">
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-base">
          <span class="flex items-center gap-1">
            {{ t('AKI') }}:
            <span class="font-semibold" :style="akiRiskLabelStyle" data-testid="infobox-aki-risk">
              {{ akiRiskLabel }}
            </span>
          </span>
          <span class="flex items-center gap-1" data-testid="infobox-trend">
            {{ t('Trend') }}:
            <VProgressCircular
              v-if="isInfoboxPanelLoading"
              indeterminate
              size="14"
              width="2"
              color="primary"
            />
            <template v-else>
              {{ trendLabel }}
              <VIcon v-if="trendIcon" :icon="trendIcon" size="18" class="material-icons-outlined" />
            </template>
          </span>
        </div>
        <span class="text-sm" data-testid="infobox-last-update">
          {{ t('Last Update') }}: {{ lastUpdateLabel }}
        </span>
      </div>
    </div>

    <hr class="border-t-thin border-[#EAECF0]" />

    <div class="flex flex-col gap-5 flex-1 overflow-y-auto min-h-0 px-6 text-sm">
      <!-- General Information -->
      <section class="flex flex-col gap-1">
        <p class="text-subtitle">
          {{ t('General Information and additional vital parameters') }}
        </p>
        <InfoRow
          v-for="row in generalInformationRows"
          :key="row.label"
          :label="row.label"
          :value="row.value"
          :loading="row.loading"
        />
      </section>

      <hr class="border-t-thin border-[#EAECF0] -mx-6" />

      <!-- AKI-Related Laboratory Samples -->
      <section class="flex flex-col gap-1">
        <p class="text-subtitle">{{ t('AKI-Related Laboratory Samples') }}</p>
        <InfoRow
          v-for="row in laboratoryRows"
          :key="row.label"
          :label="row.label"
          :value="row.value"
          :loading="row.loading"
        />
      </section>

      <hr class="border-t-thin border-[#EAECF0] -mx-6" />

      <!-- Organ Support -->
      <section class="flex justify-between items-start gap-4">
        <p class="text-subtitle">{{ t('Organ Support') }}</p>
        <VProgressCircular
          v-if="isInfoboxPanelLoading"
          indeterminate
          size="14"
          width="2"
          color="primary"
        />
        <div v-else-if="organSupportList.length" class="flex flex-col items-end gap-2">
          <span
            v-for="organSupport in organSupportList"
            :key="organSupport"
            class="flex items-center gap-1.5 rounded-full bg-white border-sm border-utility-blue-200 px-2.5 py-1 text-caption"
            data-testid="infobox-organ-support-chip"
          >
            <span class="organ-support-dot" />
            {{ organSupport }}
          </span>
        </div>
        <p v-else class="text-subtitle">{{ MISSING_VALUE }}</p>
      </section>
    </div>

    <!-- Footer -->
    <button
      class="flex items-center justify-end gap-1 px-6 text-primary font-semibold text-sm"
      data-testid="infobox-show-more"
      @click="redirectToPatientDetails"
    >
      {{ t('Show more patient details') }}
      <VIcon icon="chevron_right" size="20" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAkiPredictionsStore } from '@/stores/akiPredictions'
import { differenceInMinutes } from 'date-fns'
import type { AkiParamsItem } from '@/generated'
import type { AkiCaseWithPredictionResult } from '@/stores/akiPredictions'
import { useFormatValue } from '@/composables/useFormatValue'
import { useFormatDate } from '@/composables/useFormatDate'
import useFormattedPatientDetails from '@/composables/useFormattedPatientDetails'
import { NumberFormat } from '@/i18n/i18n'
import { parseOrganSupportDTO } from '@/util/parseOrganSupportDTO'
import InfoRow from '@/components/patient/components/atoms/AkiInfoboxRow.vue'
import { routeNames } from '@/router/routeNames'

const MISSING_VALUE = '–'

interface InfoRowData {
  label: string
  value: string
  loading?: boolean
}

const props = defineProps<{
  patientData: AkiCaseWithPredictionResult
}>()

defineEmits<{
  (e: 'close-infobox'): void
}>()

const { t } = useI18n()
const router = useRouter()
const { formatValue } = useFormatValue()
const { formatDateTime } = useFormatDate()
const { getFormattedPatientName } = useFormattedPatientDetails()
const { isInfoboxPanelLoading } = storeToRefs(useAkiPredictionsStore())

const caze = computed(() => props.patientData.case)
const usedInputs = computed(() => props.patientData.usedInputs)

// --- Formatting helpers -------------------------------------------------

function latestParam(items?: AkiParamsItem[]): AkiParamsItem | undefined {
  if (!items?.length) return undefined
  return [...items].sort(
    (a, b) => new Date(b.observationTime).getTime() - new Date(a.observationTime).getTime()
  )[0]
}

function paramValue(
  items: AkiParamsItem[] | undefined,
  numberFormat = NumberFormat.DECIMAL
): string {
  const latest = latestParam(items)
  if (!latest) return MISSING_VALUE
  const value = formatValue(latest.observationValue, numberFormat)
  return latest.unit ? `${value} ${latest.unit}` : value
}

function paramDateTime(items?: AkiParamsItem[]): string {
  return formatDateTime(latestParam(items)?.observationTime, MISSING_VALUE)
}

// --- Risk / trend / last update ----------------------------------------

const akiRiskLabel = computed(() => {
  switch (props.patientData.predictionResult?.probability) {
    case 'VERY_HIGH':
      return t('Very High Risk')
    case 'HIGH':
      return t('High Risk')
    default:
      return MISSING_VALUE
  }
})

const akiRiskLabelStyle = computed(() => {
  const colorCode = props.patientData.predictionResult?.probabilityColorCode
  return { color: colorCode ?? '#6B7280' }
})

const trendLabel = computed(() => props.patientData.trend ?? MISSING_VALUE)

const trendIcon = computed(() => {
  switch (props.patientData.trend) {
    case 'UP':
      return 'trending_up'
    case 'DOWN':
      return 'trending_down'
    case 'STEADY':
      return 'trending_flat'
    default:
      return null
  }
})

const lastUpdateLabel = computed(() => {
  const timestamp = props.patientData.predictionResult?.probabilityTimestamp
  if (!timestamp) return MISSING_VALUE
  const minutes = differenceInMinutes(new Date(), new Date(timestamp))
  return t('{minutes} min. ago', { minutes: Math.max(minutes, 0) })
})

// --- Vitals / labs ------------------------------------------------------

const generalInformationRows = computed<InfoRowData[]>(() => [
  { label: t('BMI'), value: bmiLabel.value },
  { label: t('ICU Admission'), value: admissionLabel.value },
  {
    label: t('Vasopressor Administration'),
    value: paramDateTime(props.patientData.vasopressors?.perfusionRate),
    loading: isInfoboxPanelLoading.value
  },
  { label: t('SpO₂'), value: paramValue(usedInputs.value?.vitalSigns?.spo2, NumberFormat.INTEGER) },
  {
    label: t('Temperature'),
    value: paramValue(props.patientData.temperature),
    loading: isInfoboxPanelLoading.value
  }
])

const laboratoryRows = computed<InfoRowData[]>(() => {
  const lab = usedInputs.value?.laboratory
  return [
    { label: t('Lactate'), value: paramValue(lab?.lactate) },
    { label: t('pH'), value: paramValue(lab?.ph, NumberFormat.DECIMAL) },
    { label: t('CRP'), value: paramValue(lab?.crp, NumberFormat.INTEGER) },
    { label: t('Potassium'), value: paramValue(lab?.potassium) },
    { label: t('Bilirubin'), value: paramValue(lab?.bilirubin) },
    { label: t('Leukocytes'), value: paramValue(lab?.leukocytes) },
    {
      label: t('eGFR'),
      value: paramValue(props.patientData.egfr, NumberFormat.INTEGER),
      loading: isInfoboxPanelLoading.value
    }
  ]
})

const bmiLabel = computed(() => {
  const bmi = usedInputs.value?.demography?.bmi
  return bmi != null ? `${formatValue(bmi, NumberFormat.DECIMAL)} kg/m²` : MISSING_VALUE
})

const admissionLabel = computed(() =>
  formatDateTime(usedInputs.value?.demography?.admissionDate, MISSING_VALUE)
)

const organSupportList = computed<string[]>(() => {
  if (!props.patientData.organSupport) return []
  return parseOrganSupportDTO(props.patientData.organSupport, t)
})

async function redirectToPatientDetails() {
  await router.push({
    name: routeNames.PATIENT_DETAILS,
    params: { caseId: caze.value.id }
  })
}
</script>

<style scoped lang="scss">
.organ-support-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #17b26a;
}
</style>
